/**
 * The Preview toolbar's commands, grouped by intent (Issue #2542).
 *
 * The toolbar used to be a row of thirteen buttons. It now shows three menus — Zoom, Page and
 * Tools — and every command lives in exactly one of them. This file is the single list of those
 * commands: the toolbar template reads labels, icons, shortcuts and test IDs from it, and the
 * Preview receives the command's id. Neither the list nor the toolbar contains any measurement,
 * crop, calibration or rotation logic; the Preview decides what each command does.
 */

import { AreaShape } from '../measure-overlay/area-geometry';

/** The three toolbar menus. */
export type PreviewCommandGroup = 'zoom' | 'page' | 'tools';

/** Every command the toolbar can send to the Preview. */
export type PreviewCommandId =
  // Zoom
  | 'zoomIn'
  | 'zoomOut'
  | 'setZoom'
  | 'fitToViewport'
  | 'fitToWidth'
  | 'fitToHeight'
  // Page
  | 'rotateLeft'
  | 'rotateRight'
  | 'calibrateScale'
  | 'showScaleDetails'
  | 'toggleScaleCompare'
  | 'resetScale'
  | 'openInNewTab'
  // Tools
  | 'toggleRuler'
  | 'toggleArea'
  | 'selectAreaShape'
  | 'exitTool';

/** What the toolbar knows about one command. */
export interface PreviewCommandDescriptor {
  id: PreviewCommandId;
  group: PreviewCommandGroup;
  /** Visible label in the menu, which is also the accessible name. */
  label: string;
  /** Material icon name. */
  icon: string;
  /** Icon font set, when the icon is not in the default Material Icons font. */
  fontSet?: string;
  /** Stable test ID; kept from the pre-#2542 toolbar so deployed journeys keep finding commands. */
  testId: string;
  /**
   * Keyboard shortcut, shown next to the label and pressed on the Preview (Issue #3313). The key
   * map in `preview-shortcuts.ts` is built from these, so the menu and the keys cannot disagree.
   */
  shortcut?: string;
  /**
   * True for commands that change the saved page (rotation, resetting a calibration). The deployed
   * read-only journey uses this marker to leave them alone.
   */
  persistent?: boolean;
  /**
   * True for commands that need edit rights on the project: they change the saved page or start a
   * calibration whose purpose is to save one (Issue #3313). The server refuses them for a reader,
   * so the Preview does not offer them to one.
   */
  needsEditRights?: boolean;
  /** True when choosing the command leaves its menu open, so it can be repeated quickly. */
  keepsMenuOpen?: boolean;
}

/** All toolbar commands, in menu order. */
export const PREVIEW_COMMANDS: readonly PreviewCommandDescriptor[] = [
  {
    id: 'zoomIn',
    group: 'zoom',
    label: 'Zoom in',
    icon: 'zoom_in',
    testId: 'preview-zoom-in',
    shortcut: '+',
    keepsMenuOpen: true,
  },
  {
    id: 'zoomOut',
    group: 'zoom',
    label: 'Zoom out',
    icon: 'zoom_out',
    testId: 'preview-zoom-out',
    shortcut: '−',
    keepsMenuOpen: true,
  },
  {
    id: 'fitToViewport',
    group: 'zoom',
    label: 'Fit to viewport',
    icon: 'fit_screen',
    testId: 'preview-fit-viewport',
    shortcut: '0',
  },
  {
    id: 'fitToWidth',
    group: 'zoom',
    label: 'Fit to width',
    icon: 'arrow_range',
    fontSet: 'material-symbols-outlined',
    testId: 'preview-fit-width',
    shortcut: 'W',
  },
  {
    id: 'fitToHeight',
    group: 'zoom',
    label: 'Fit to height',
    icon: 'height',
    testId: 'preview-fit-height',
    shortcut: 'H',
  },
  {
    id: 'rotateLeft',
    group: 'page',
    label: 'Rotate page left',
    icon: 'rotate_left',
    testId: 'preview-rotate-left',
    shortcut: 'Shift+R',
    persistent: true,
    needsEditRights: true,
  },
  {
    id: 'rotateRight',
    group: 'page',
    label: 'Rotate page right',
    icon: 'rotate_right',
    testId: 'preview-rotate-right',
    shortcut: 'R',
    persistent: true,
    needsEditRights: true,
  },
  {
    id: 'calibrateScale',
    group: 'page',
    label: 'Calibrate scale',
    icon: 'architecture',
    testId: 'preview-calibrate-scale',
    shortcut: 'C',
    needsEditRights: true,
  },
  {
    id: 'showScaleDetails',
    group: 'page',
    label: 'Calibration details',
    icon: 'info',
    testId: 'preview-scale-details',
  },
  {
    id: 'toggleScaleCompare',
    group: 'page',
    label: 'Compare with detected scale',
    icon: 'compare',
    testId: 'preview-scale-compare',
  },
  {
    id: 'resetScale',
    group: 'page',
    label: 'Reset to automatic',
    icon: 'restart_alt',
    testId: 'preview-scale-reset',
    persistent: true,
    needsEditRights: true,
  },
  {
    id: 'openInNewTab',
    group: 'page',
    label: 'Open PDF in new tab',
    icon: 'open_in_new',
    testId: 'preview-open-new-tab',
  },
  { id: 'toggleRuler', group: 'tools', label: 'Ruler', icon: 'straighten', testId: 'preview-ruler', shortcut: 'M' },
  {
    id: 'toggleArea',
    group: 'tools',
    label: 'Select area',
    icon: 'highlight_alt',
    testId: 'preview-area-selector',
    shortcut: 'A',
  },
  // Escape is handled by the active tool, which undoes one level at a time before it exits.
  {
    id: 'exitTool',
    group: 'tools',
    label: 'Exit tool',
    icon: 'close',
    testId: 'preview-tool-exit-menu',
    shortcut: 'Esc',
  },
];

const BY_ID = new Map(PREVIEW_COMMANDS.map(command => [command.id, command]));

/** The descriptor of one command. Throws for an unknown id, which can only be a programming error. */
export function previewCommand(id: PreviewCommandId): PreviewCommandDescriptor {
  const command = BY_ID.get(id);
  if (!command) throw new Error(`Unknown Preview command: ${id}`);
  return command;
}

/** True for a command a project reader may not run (see {@link PreviewCommandDescriptor.needsEditRights}). */
export function previewCommandNeedsEditRights(id: PreviewCommandId): boolean {
  return !!BY_ID.get(id)?.needsEditRights;
}

/** The commands of one menu, in menu order. */
export function previewCommandsIn(group: PreviewCommandGroup): PreviewCommandDescriptor[] {
  return PREVIEW_COMMANDS.filter(command => command.group === group);
}

/** The fit mode a page can be fitted to from the Zoom menu. */
export type PreviewFitMode = 'viewport' | 'width' | 'height';

/** The geometry tool that is active in the Preview, if any. */
export type PreviewTool = 'ruler' | 'area' | null;

/**
 * What a ruler line is being used for. A line is drawn the same way for both; `scaleReference`
 * means the line is the reference of a manual scale calibration (Issue #2442), which adds endpoint
 * handles and the calibration form.
 */
export type LineOperation = 'measure' | 'scaleReference';

/**
 * A shape Select area can draw using the cumulative area tool.
 */
export type PreviewAreaShape = AreaShape;

/** What the Tools menu shows for one Select area shape. */
export interface PreviewAreaShapeOption {
  shape: PreviewAreaShape;
  label: string;
  icon: string;
  /** Stable test ID; the first four keep the IDs of the pre-#2542 area shape menu. */
  testId: string;
  /** Keyboard shortcut, as on {@link PreviewCommandDescriptor.shortcut} (Issue #3313). */
  shortcut?: string;
}

/** The Select area shapes, in menu order. */
export const PREVIEW_AREA_SHAPES: readonly PreviewAreaShapeOption[] = [
  { shape: 'rectangle', label: 'Rectangle', icon: 'crop_free', testId: 'preview-area-shape-rectangle' },
  { shape: 'ellipse', label: 'Oval', icon: 'circle', testId: 'preview-area-shape-ellipse' },
  { shape: 'polygon', label: 'Polygon', icon: 'pentagon', testId: 'preview-area-shape-polygon' },
  { shape: 'freehand', label: 'Freehand', icon: 'gesture', testId: 'preview-area-shape-freehand' },
  { shape: 'select', label: 'Select & move', icon: 'open_with', testId: 'preview-area-shape-select', shortcut: 'V' },
];

/** The menu entry of one Select area shape. */
export function previewAreaShape(shape: PreviewAreaShape): PreviewAreaShapeOption {
  return PREVIEW_AREA_SHAPES.find(option => option.shape === shape) ?? PREVIEW_AREA_SHAPES[0];
}

/**
 * The Preview's geometry interaction as one value (Issue #2542), replacing the separate ruler,
 * area, calibrate and region-chat flags. Drawing progress inside a session (drawing, complete) is
 * held by the overlay or selection store that draws it.
 *
 * An area interaction names the shape being drawn. `chatReview` is true while the chat attachment
 * of one measured area is being reviewed; the areas stay on screen underneath and cannot be edited
 * until the review closes.
 */
export type PreviewInteraction =
  | { kind: 'idle' }
  | { kind: 'line'; operation: LineOperation }
  | { kind: 'area'; shape: PreviewAreaShape; chatReview?: boolean };

export const IDLE_INTERACTION: PreviewInteraction = { kind: 'idle' };

/** The tool that an interaction belongs to. */
export function toolOf(interaction: PreviewInteraction): PreviewTool {
  if (interaction.kind === 'line') return 'ruler';
  if (interaction.kind === 'area') return 'area';
  return null;
}

/**
 * The interaction after a tool command from the Tools menu.
 *
 * Choosing the active tool again leaves it; choosing the other tool switches to it; Exit tool
 * always returns to idle. A ruler chosen from the menu always measures; calibration is entered with
 * {@link startScaleReference}. Select area starts with the shape the reviewer used last.
 */
export function applyToolCommand(
  interaction: PreviewInteraction,
  command: 'toggleRuler' | 'toggleArea' | 'exitTool',
  areaShape: PreviewAreaShape = 'rectangle'
): PreviewInteraction {
  if (command === 'exitTool') return IDLE_INTERACTION;
  const tool: PreviewTool = command === 'toggleRuler' ? 'ruler' : 'area';
  if (toolOf(interaction) === tool) {
    // The ruler chosen again while it holds a calibration draft goes back to plain measuring
    // rather than closing, so the reviewer keeps the line they drew.
    if (interaction.kind === 'line' && interaction.operation === 'scaleReference') {
      return { kind: 'line', operation: 'measure' };
    }
    return IDLE_INTERACTION;
  }
  return tool === 'ruler' ? { kind: 'line', operation: 'measure' } : { kind: 'area', shape: areaShape };
}

/**
 * The interaction after a shape is chosen in the Tools menu: Select area with that shape. Choosing
 * a shape never leaves the tool, so the shape that is already active simply stays.
 */
export function applyAreaShape(shape: PreviewAreaShape): PreviewInteraction {
  return { kind: 'area', shape };
}

/** The ruler session with its line used as a scale reference. */
export function startScaleReference(): PreviewInteraction {
  return { kind: 'line', operation: 'scaleReference' };
}

/** Short name of the active tool for the Tools trigger. */
export function interactionLabel(interaction: PreviewInteraction): string {
  if (interaction.kind === 'line') return interaction.operation === 'scaleReference' ? 'Calibrating' : 'Ruler';
  if (interaction.kind === 'area') return 'Select area';
  return 'Tools';
}
